'use client'

import { useEffect, useState } from 'react'
import { FaTimes, FaGem, FaMarker } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import Portal from './Portal'
import { isUrl } from '../helpers/helpers'

// Gem price of one tweak — keep in sync with TWEAK_GEM_COST in
// promptchan-backend/blueprints/tweak_bp.py.
export const TWEAK_GEM_COST = 3

// Image "Tweak" modal (Qwen-Edit): type what to change about an existing result
// and submit. Pure controlled component like AnimateModal — it only collects the
// edit prompt and hands it to `onConfirm`; the caller (Create.jsx on /generate)
// owns the generation call.
export default function TweakModal({
  isOpen,
  closeModal,
  source, // { url, isUrl, docRef }
  onConfirm,
}) {
  const { t } = useTranslation()

  const [shouldRender, setShouldRender] = useState(isOpen)
  const [tweakPrompt, setTweakPrompt] = useState('')

  // Reset the draft each time the modal opens.
  useEffect(() => {
    if (!isOpen) return
    setShouldRender(true)
    setTweakPrompt('')
  }, [isOpen, source])

  if (!shouldRender) return null

  const thumb = source?.url
    ? isUrl(source.url)
      ? source.url
      : `data:image/jpeg;base64,${source.url}`
    : null

  const handleTweak = () => {
    const prompt = tweakPrompt.trim()
    if (!prompt) return
    onConfirm?.({ prompt })
    closeModal()
  }

  return (
    <Portal>
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) closeModal()
        }}
        className={`fixed z-[110] top-0 left-0 w-full h-full bg-black/70 p-3 flex justify-center items-center transition-opacity duration-200 ${
          isOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className={`bg-[var(--middle-bg-color)] max-h-[85vh] w-full max-w-md flex flex-col overflow-hidden min-h-0 rounded-2xl border border-white/10 shadow-2xl p-5 text-gray-100 space-y-3 relative transform transition-all duration-200 ${
            isOpen ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
          }`}
        >
          <header>
            <h2 className="flex items-center gap-2 text-2xl font-extrabold text-shadow">
              <FaMarker className="text-[var(--main-bg-color)]" />
              {t('tweak', 'Tweak')}
            </h2>
            <h3 className="mt-1 text-sm font-medium text-gray-400">
              {t('tweakSubtitle', 'Describe what you want to change')}
            </h3>
            <button
              onClick={closeModal}
              aria-label={t('close')}
              className="absolute right-3 top-3 text-white/60 transition-colors hover:text-white"
              style={{ fontSize: '1.4rem' }}
            >
              <FaTimes />
            </button>
          </header>
          <hr className="border-gray-800" />

          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            {thumb && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={thumb}
                alt=""
                className="mx-auto max-h-40 w-auto rounded-xl object-contain"
              />
            )}

            <div>
              <textarea
                id="tweakPrompt"
                value={tweakPrompt}
                onChange={(e) => setTweakPrompt(e.target.value)}
                placeholder={t(
                  'tweakPlaceholder',
                  'e.g. Change their hair colour to brunette',
                )}
                maxLength={500}
                autoFocus
                className="w-full h-24 rounded-2xl px-4 py-3 text-white bg-[var(--middle-bg-color)] border border-[var(--foreground-bg-color)] focus:border-white focus:outline-none focus:ring-2 focus:ring-white/15 transition"
              />
              <p className="mt-1.5 text-xs text-gray-400 leading-relaxed">
                {t(
                  'tweakHint',
                  'Change outfits, background, pose, lighting and more — the rest of the image stays the same.',
                )}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleTweak}
            disabled={!tweakPrompt.trim()}
            className="shrink-0 w-full h-12 rounded-2xl bg-[var(--main-bg-color)] text-white font-semibold inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.01] active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-40 disabled:pointer-events-none"
          >
            <FaMarker size={15} />
            {t('tweak', 'Tweak')}
            <span className="inline-flex items-center ml-1 text-white/90">
              <FaGem size={11} className="mr-0.5" />
              <span className="font-semibold text-sm">{TWEAK_GEM_COST}</span>
            </span>
          </button>
        </div>
      </div>
    </Portal>
  )
}
